import { MainApi } from 'api/MainApi'
import Loader from 'components/Loader/Loader'
import { useEffectOnce } from 'hooks/useEffectOnce'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useEffect, useRef, useState } from 'react'
import { PROGRESS_TYPES } from 'utils/constants'
import { ICleaningZone, IRecordedRoute, IRoute } from 'utils/interfaces'
import { optionalIntParam, pageParam, stringParam } from 'utils/urlQueryState'

import RecordedRouteListView from './RecordedRouteListView/RecordedRouteListView'

const RecordedRouteList = () => {
  const [url, setUrl] = useUrlQueryState({
    q: stringParam(),
    page: pageParam(),
    route: optionalIntParam(),
  })
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [recordedRoutes, setRecordedRoutes] = useState<IRecordedRoute[]>([])
  const [facilities, setFacilities] = useState<any[]>([])
  const currentPage = url.page
  const [totalCount, setTotalCount] = useState(0)
  const searchQuery = url.q
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(url.q)
  const skipPageResetRef = useRef(true)
  const PAGE_SIZE = 20

  const fetchData = async (page: number = currentPage, search: string = debouncedSearchQuery) => {
    try {
      const response = await MainApi.fetchRecordedRoutes(page, PAGE_SIZE, search || undefined)
      setRecordedRoutes(response.results)
      setTotalCount(response.count)
    } catch (error) {
      setLoadingProgress(PROGRESS_TYPES.ERROR)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    if (loadingProgress === PROGRESS_TYPES.SUCCESS) {
      if (!skipPageResetRef.current && currentPage !== 1) {
        setUrl({ page: 1 })
        fetchData(1, debouncedSearchQuery)
        return
      }
      fetchData(currentPage, debouncedSearchQuery)
    }
  }, [debouncedSearchQuery])

  useEffect(() => {
    skipPageResetRef.current = false
  }, [])

  useEffectOnce(() => {
    const initialize = async () => {
      setLoadingProgress(PROGRESS_TYPES.WORK)
      try {
        const facRes = await MainApi.fetchFacilities(1, 1000)
        setFacilities(facRes.results)
      } catch (e) {
        console.error('Failed to fetch facilities', e)
      }
      await fetchData()
      setLoadingProgress(PROGRESS_TYPES.SUCCESS)
    }
    initialize()
  })

  const onPageChanged = (page: number) => {
    setUrl({ page })
    fetchData(page)
  }

  const onSearchChanged = (search: string) => {
    setUrl({ q: search })
  }

  const stateList = {
    [`${PROGRESS_TYPES.SUCCESS}`]: (
      <RecordedRouteListView
        recordedRoutes={recordedRoutes}
        facilities={facilities}
        onRoutesChanged={() => fetchData()}
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={PAGE_SIZE}
        onPageChanged={onPageChanged}
        searchQuery={searchQuery}
        onSearchChanged={onSearchChanged}
        selectedRouteId={url.route}
        onSelectedRouteIdChange={id => setUrl({ route: id })}
      />
    ),
    [`${PROGRESS_TYPES.WORK}`]: <Loader />,
    [`${PROGRESS_TYPES.ERROR}`]: <div>Error</div>,
    [`${PROGRESS_TYPES.IDLE}`]: null,
  }

  return stateList[loadingProgress] || null
}

export default RecordedRouteList
